
import { WordPair } from "@/types/game";
import { calculateProximityScore } from "@/utils/claude/proximityCalculator";
import { supabase } from "@/integrations/supabase/client";

/**
 * Calculate proximity score and update the database and state
 */
export const calculateAndUpdateProximity = async (
  gameId: string,
  roundNumber: number,
  userWord: string,
  aiWord: string,
  setRounds: (rounds: WordPair[] | ((prev: WordPair[]) => WordPair[])) => void
) => {
  try {
    // Add retry logic for proximity calculation
    let proximityScore = 50; // Default score
    let attempts = 0;
    const maxAttempts = 2;
    let success = false;
    
    while (attempts < maxAttempts && !success) {
      attempts++;
      try {
        // Calculate proximity score using Claude, passing game info for logging
        proximityScore = await calculateProximityScore(userWord, aiWord, gameId, roundNumber);
        console.log(`Calculated proximity score: ${proximityScore} for round ${roundNumber} (attempt ${attempts})`);
        success = true;
      } catch (error) {
        console.error(`Error calculating proximity score (attempt ${attempts}):`, error);
        
        if (attempts < maxAttempts) {
          // Wait before retry
          console.log(`Waiting 1 second before retry...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
        } else {
          console.warn(`Max attempts (${maxAttempts}) reached, using default proximity score`);
        }
      }
    }
    
    // Update the database with the proximity score
    const { error: proximityError } = await supabase
      .from("rounds")
      .update({ 
        proximity_score: proximityScore
      })
      .eq("game_id", gameId)
      .eq("round_number", roundNumber);
      
    if (proximityError) {
      console.error("Error updating proximity score:", proximityError);
      // Non-blocking error - continue even if this fails
    }
    
    // Update the rounds state with the proximity score
    setRounds((prevRounds: WordPair[]) => {
      const updatedRounds = [...prevRounds];
      if (updatedRounds.length >= roundNumber) {
        // Update the specific round with the proximity score
        updatedRounds[roundNumber - 1] = {
          ...updatedRounds[roundNumber - 1],
          proximityScore
        };
      }
      return updatedRounds;
    });
    
    return proximityScore;
  } catch (error) {
    console.error("Error calculating and updating proximity:", error);
    // If there's an error, use a default proximity score
    return 50;
  }
};
